/**
 * v3.15-1104 — Send-to-CF360 Modal
 *
 * Per JOINT_IMPLEMENTATION_PLAN §2.2 + JOINT_HUB_CF360_LIFECYCLE_ALIGNMENT
 * §4.1. Operator confirmation flow:
 *
 *   1. Verify client identity (display name + last 4 of SSN if available).
 *   2. Operator types the client's LAST NAME (case-insensitive match).
 *   3. Optional free-text "credit concerns" note (max 2000 chars).
 *   4. If the client was previously returned from CF360, show a prior-
 *      history acknowledgement that must be ticked.
 *   5. POST /api/clients/{id}/send-to-cf360.
 *   6. On success, parent refetches the client; the dormant banner
 *      appears in the same render.
 *
 * Layer-1 of the 4-layer duplicate prevention defence — the parent
 * disables the "Send to CF360" button whenever the client is already
 * in_cf360 or in a terminal state. This modal does not re-derive
 * eligibility; it trusts the trigger.
 */
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export const SendToCF360Modal = ({ open, onOpenChange, client, onSuccess }) => {
  const [confirmLastName, setConfirmLastName] = useState('');
  const [creditConcerns, setCreditConcerns] = useState('');
  const [acknowledgedPriorHistory, setAcknowledgedPriorHistory] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const wasPreviouslyReturned = client?.cf360_state === 'returned';

  const expectedLastName = useMemo(() => {
    const full = (client?.full_name || '').trim();
    if (!full) return '';
    const parts = full.split(/\s+/);
    return parts[parts.length - 1] || '';
  }, [client]);

  // Reset state whenever the modal re-opens
  useEffect(() => {
    if (open) {
      setConfirmLastName('');
      setCreditConcerns('');
      setAcknowledgedPriorHistory(false);
    }
  }, [open]);

  const lastNameMatches =
    expectedLastName &&
    confirmLastName.trim().toLowerCase() === expectedLastName.toLowerCase();

  const canSubmit =
    lastNameMatches &&
    !submitting &&
    (!wasPreviouslyReturned || acknowledgedPriorHistory);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/clients/${client.id}/send-to-cf360`, {
        confirm_last_name: confirmLastName,
        credit_concerns: creditConcerns || null,
        acknowledged_prior_history: !!acknowledgedPriorHistory,
      });
      const d = res?.data;
      if (d?.success) {
        toast.success(d?.message || 'Client sent to CF360.');
        onOpenChange?.(false);
        onSuccess?.(d);
      } else {
        toast.error(d?.message || 'Send to CF360 did not complete.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.message || err?.message;
      toast.error(`Send to CF360 failed: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="send-to-cf360-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-600" />
            Send Client to CF360
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
            <div className="text-xs text-slate-500 uppercase tracking-wide">
              You are sending
            </div>
            <div className="font-semibold text-slate-900 mt-0.5">
              {client?.full_name || '—'}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {client?.email || ''}{client?.phone ? ` · ${client.phone}` : ''}
            </div>
          </div>

          {wasPreviouslyReturned && (
            <div
              data-testid="cf360-send-prior-history-warning"
              className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-950 text-xs"
            >
              <div className="font-semibold mb-1">Prior CF360 history</div>
              <div>
                This client was previously in CF360 (returned{' '}
                {client?.cf360_returned_at?.slice(0, 10) || 'previously'}).
                Sending again will start a <strong>new</strong> CF360 file.
                The prior cycle remains in the timeline.
              </div>
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="cf360-send-prior-history-ack"
                  checked={acknowledgedPriorHistory}
                  onChange={(e) => setAcknowledgedPriorHistory(e.target.checked)}
                  className="accent-amber-600"
                />
                <span>I acknowledge the prior history and want to proceed.</span>
              </label>
            </div>
          )}

          <div>
            <Label htmlFor="cf360-confirm-last-name" className="text-xs">
              Type the client&apos;s last name to confirm
            </Label>
            <Input
              id="cf360-confirm-last-name"
              data-testid="cf360-confirm-last-name-input"
              autoComplete="off"
              autoCorrect="off"
              value={confirmLastName}
              onChange={(e) => setConfirmLastName(e.target.value)}
              placeholder={expectedLastName ? '' : 'No last name on record'}
              disabled={!expectedLastName || submitting}
            />
            {confirmLastName && !lastNameMatches && (
              <div className="text-xs text-rose-700 mt-1">
                Doesn&apos;t match the client&apos;s last name.
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="cf360-credit-concerns" className="text-xs">
              Credit concerns (optional)
            </Label>
            <Textarea
              id="cf360-credit-concerns"
              data-testid="cf360-credit-concerns-input"
              rows={3}
              maxLength={2000}
              value={creditConcerns}
              onChange={(e) => setCreditConcerns(e.target.value)}
              placeholder="e.g., recent collection, charge-offs, dispute needed&hellip;"
              disabled={submitting}
            />
            <div className="text-[10px] text-slate-400 text-right mt-0.5">
              {creditConcerns.length}/2000
            </div>
          </div>

          <div className="text-xs text-slate-500 border-t pt-3">
            All Hub automations and funding workflows pause for this client
            once sent. The client returns to active status automatically
            when CF360 marks the file complete.
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={submitting}
            data-testid="cf360-send-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="cf360-send-confirm"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <ShieldAlert className="h-4 w-4 mr-1" />
            )}
            Send to CF360
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SendToCF360Modal;
